import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import ClientForm from "@/components/ClientForm";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Download, MapPin, Mail, Phone } from "lucide-react";

export default function Clients() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = async () => {
    const res = await endpoints.list("clients");
    setClients(res.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (search.get("new") === "1") {
      setEditing(null);
      setOpen(true);
    }
  }, [search]);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      const matchQ = !query ||
        `${c.first_name} ${c.last_name} ${c.email} ${c.phone} ${c.street} ${c.neighborhood}`.toLowerCase().includes(query.toLowerCase());
      const matchT = typeFilter === "all" || c.type === typeFilter;
      return matchQ && matchT;
    });
  }, [clients, query, typeFilter]);

  const saveClient = async (data, nextAction) => {
    try {
      let saved;
      if (editing) {
        const res = await endpoints.update("clients", editing.id, data);
        saved = res.data;
        toast.success("Client updated");
      } else {
        const res = await endpoints.create("clients", data);
        saved = res.data;
        toast.success("Client created");
      }
      setOpen(false);
      setEditing(null);
      await load();
      if (nextAction === "estimate") navigate(`/estimates?new=1&client=${saved.id}`);
      else if (nextAction === "job") navigate(`/jobs?new=1&client=${saved.id}`);
    } catch (err) {
      toast.error("Failed to save client");
    }
  };

  const removeClient = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await endpoints.remove("clients", id);
    toast.success("Client deleted");
    load();
  };

  const exportCSV = () => {
    const headers = ["First Name","Last Name","Phone","Email","Type","Street","City","State","ZIP"];
    const rows = filtered.map(c => [c.first_name, c.last_name, c.phone, c.email, c.type, c.street, c.city, c.state, c.zip]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${(v||"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "clients.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout
      title="Clients"
      subtitle={`${clients.length} customer profiles`}
    >
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="clients-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="clients-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Residential">Residential</SelectItem>
            <SelectItem value="Commercial">Commercial</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={exportCSV} data-testid="clients-export">
          <Download className="size-4 mr-1.5"/>Export CSV
        </Button>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="clients-new">
          <Plus className="size-4 mr-1.5"/>New Client
        </Button>
      </div>

      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="bg-muted/40">
              <tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
                <th className="p-4 font-semibold">Name</th>
                <th className="p-4 font-semibold">Contact</th>
                <th className="p-4 font-semibold">Address</th>
                <th className="p-4 font-semibold">Type</th>
                <th className="p-4 font-semibold">Tier</th>
                <th className="p-4 font-semibold w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="p-10 text-center text-muted-foreground">No clients found.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                  <td className="p-4">
                    <div className="font-semibold">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-muted-foreground">{c.neighborhood}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-xs flex items-center gap-1.5"><Mail className="size-3"/>{c.email}</div>
                    <div className="text-xs flex items-center gap-1.5 text-muted-foreground"><Phone className="size-3"/>{c.phone}</div>
                  </td>
                  <td className="p-4 text-xs">
                    <div className="flex items-start gap-1.5"><MapPin className="size-3 mt-0.5"/><div>{c.street}<br/>{c.city}, {c.state} {c.zip}</div></div>
                  </td>
                  <td className="p-4"><StatusPill status={c.type === "Commercial" ? "Confirmed" : "Completed"} /></td>
                  <td className="p-4"><span className="text-xs font-semibold">{c.tier || "Standard"}</span></td>
                  <td className="p-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="size-8" data-testid={`client-actions-${c.id}`}>
                          <MoreHorizontal className="size-4"/>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(c); setOpen(true); }}><Pencil className="size-3.5 mr-2"/>Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/estimates?new=1&client=${c.id}`)}><Plus className="size-3.5 mr-2"/>New Estimate</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/jobs?new=1&client=${c.id}`)}><Plus className="size-3.5 mr-2"/>Schedule Job</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => removeClient(c.id)} className="text-destructive"><Trash2 className="size-3.5 mr-2"/>Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ClientForm
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}
        onSaved={saveClient}
        initial={editing}
      />
    </Layout>
  );
}
